// Serviço de Autenticação - Frontend
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Chaves do localStorage
const TOKEN_KEY = 'clickhub_token';
const REFRESH_KEY = 'clickhub_refresh';
const USER_KEY = 'clickhub_user';

export const authService = {
  // ======================
  // REGISTRO
  // ======================
  
  async registrar(dados) {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/registro`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          nome: dados.nome,
          email: dados.email,
          senha: dados.senha,
          confirmar_senha: dados.confirmarSenha,
          telefone: dados.telefone || null,
          tipo_usuario: dados.tipoUsuario || 'afiliado'
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao criar conta');
      }
      
      const data = await response.json();
      
      // Salvar tokens
      this.salvarTokens(data);
      
      return data;
    } catch (error) {
      console.error('Erro no registro:', error);
      throw error;
    }
  },
  
  // ======================
  // LOGIN
  // ======================
  
  async login(email, senha) {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, senha })
      });
      
      const data = await response.json().catch(() => null);
      
      if (!response.ok) {
        throw new Error(data?.detail || 'Email ou senha incorretos');
      }
      
      this.salvarTokens(data);
      return data;
    } catch (error) {
      if (error.message?.includes('fetch') || error.message?.includes('network') || error.message?.includes('Failed')) {
        throw new Error('Erro de conexão. Tente novamente.');
      }
      throw error;
    }
  },
  
  // ======================
  // LOGOUT
  // ======================
  
  async logout() {
    try {
      const token = this.getToken();
      if (token) {
        await fetch(`${BACKEND_URL}/api/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
      }
    } catch (error) {
      console.error('Erro no logout:', error);
    } finally {
      this.limparTokens();
    }
  },
  
  // ======================
  // REFRESH TOKEN
  // ======================
  
  async refreshToken() {
    try {
      const refreshToken = localStorage.getItem(REFRESH_KEY);
      if (!refreshToken) {
        throw new Error('Sem refresh token');
      }
      
      const response = await fetch(`${BACKEND_URL}/api/auth/refresh`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken })
      });
      
      if (!response.ok) {
        this.limparTokens();
        throw new Error('Sessão expirada');
      }
      
      const data = await response.json();
      this.salvarTokens(data);
      
      return data;
    } catch (error) {
      console.error('Erro ao renovar token:', error);
      this.limparTokens();
      throw error;
    }
  },
  
  // ======================
  // PERFIL
  // ======================
  
  async getPerfil() {
    try {
      const response = await this.fetchAuth(`${BACKEND_URL}/api/auth/me`);
      if (!response.ok) throw new Error('Erro ao obter perfil');
      return await response.json();
    } catch (error) {
      console.error('Erro ao obter perfil:', error);
      throw error;
    }
  },
  
  async atualizarPerfil(dados) {
    try {
      const response = await this.fetchAuth(`${BACKEND_URL}/api/auth/me`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dados)
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao atualizar perfil');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Erro ao atualizar perfil:', error);
      throw error;
    }
  },
  
  // ======================
  // SENHA
  // ======================
  
  async alterarSenha(senhaAtual, novaSenha, confirmarSenha) {
    try {
      const response = await this.fetchAuth(`${BACKEND_URL}/api/auth/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          senha_atual: senhaAtual,
          nova_senha: novaSenha,
          confirmar_senha: confirmarSenha
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao alterar senha');
      }
      
      // Logout após alterar senha
      this.limparTokens();
      
      return await response.json();
    } catch (error) {
      console.error('Erro ao alterar senha:', error);
      throw error;
    }
  },
  
  async esqueciSenha(email) {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email })
      });
      
      return await response.json();
    } catch (error) {
      console.error('Erro ao solicitar recuperação:', error);
      throw error;
    }
  },
  
  async resetarSenha(token, novaSenha, confirmarSenha) {
    try {
      const response = await fetch(`${BACKEND_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          nova_senha: novaSenha,
          confirmar_senha: confirmarSenha
        })
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Erro ao resetar senha');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Erro ao resetar senha:', error);
      throw error;
    }
  },
  
  // ======================
  // HELPERS
  // ======================
  
  salvarTokens(data) {
    localStorage.setItem(TOKEN_KEY, data.access_token);
    localStorage.setItem(REFRESH_KEY, data.refresh_token);
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
  },
  
  limparTokens() {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_KEY);
    localStorage.removeItem(USER_KEY);
  },
  
  getToken() {
    return localStorage.getItem(TOKEN_KEY);
  },
  
  getUser() {
    const userStr = localStorage.getItem(USER_KEY);
    try { return userStr ? JSON.parse(userStr) : null; } catch (e) { localStorage.removeItem(USER_KEY); return null; }
  },
  
  isLoggedIn() {
    return !!this.getToken();
  },
  
  // Fetch com token automático e refresh
  async fetchAuth(url, options = {}) {
    let token = this.getToken();
    
    if (!token) {
      throw new Error('Não autenticado');
    }
    
    options.headers = {
      ...options.headers,
      'Authorization': `Bearer ${token}`
    };
    
    let response = await fetch(url, options);
    
    // Se token expirou, tenta renovar
    if (response.status === 401) {
      try {
        await this.refreshToken();
        token = this.getToken();
        options.headers['Authorization'] = `Bearer ${token}`;
        response = await fetch(url, options);
      } catch (error) {
        // Refresh falhou, limpar tokens
        this.limparTokens();
        throw new Error('Sessão expirada. Faça login novamente.');
      }
    }
    
    return response;
  }
};

export default authService;
